/**
 * UAE Corporate Tax estimation (ARCHITECTURE §3.1). A 9% rate applies to
 * taxable income above AED 375,000; Small Business Relief can zero the charge
 * when revenue is at or below a ceiling. This is an estimate, not a filing.
 */
import type { Money } from "./types";
import { scale, zero } from "./money";
import type { CtCategory } from "./types";

export interface CtInputs {
  /** Accounting profit before tax, in base minor units. */
  readonly profitBeforeTax: Money;
  /** Total revenue, used for the Small Business Relief test. */
  readonly revenue: Money;
  readonly rate: number;
  /** Tax-free threshold in base minor units (0% at or below it). */
  readonly threshold: number;
  /** SBR revenue ceiling in base minor units, or null when disabled. */
  readonly sbReliefRevenueCeiling: number | null;
}

export interface CtEstimate {
  readonly taxableAboveThreshold: Money;
  readonly provision: Money;
  readonly reliefApplied: boolean;
}

/**
 * Estimate the corporate-tax provision. When Small Business Relief applies
 * (revenue ≤ ceiling), the provision is zero. Otherwise 9% of profit above the
 * threshold (never negative).
 */
export function estimateCt(inputs: CtInputs): CtEstimate {
  const { profitBeforeTax, revenue, rate, threshold, sbReliefRevenueCeiling } = inputs;
  const base = profitBeforeTax.currency;

  const reliefApplied =
    sbReliefRevenueCeiling !== null && revenue.amount <= sbReliefRevenueCeiling;

  if (reliefApplied) {
    return { taxableAboveThreshold: zero(base), provision: zero(base), reliefApplied };
  }

  const above = Math.max(0, profitBeforeTax.amount - threshold);
  const taxableAboveThreshold: Money = { amount: above, currency: base };
  const provision = scale(taxableAboveThreshold, rate);
  return { taxableAboveThreshold, provision, reliefApplied };
}

const LABELS: Record<CtCategory, string> = {
  deductible: "Deductible",
  "non-deductible": "Non-deductible",
  relief: "Relief-flagged",
};

export function ctCategoryLabel(cat: CtCategory): string {
  return LABELS[cat];
}
